// Session state machine — drives what the Shell renders (F-33 fix).
//
// States:
//   'signed-out'    — no token yet; show the Google sign-in button only.
//   'authenticating'— token present, /api/me in flight.
//   'signed-in'     — /api/me succeeded and mapped:true; role is real.
//   'no-access'     — /api/me succeeded but mapped:false (INV-9: named, not blank).
//   'fault'         — /api/me rejected (INV-9: named fault, not blank).
import { signal } from '@preact/signals';
import { authToken, setToken } from './auth';
import { clearStore } from './store';
import { forgetDataVersions } from './dataVersions';
import { fetchMe, HttpError, type MeResponse } from './api';

export type SessionStatus = 'signed-out' | 'authenticating' | 'signed-in' | 'no-access' | 'fault';

export interface SessionState {
  status: SessionStatus;
  me: MeResponse | null;
  error: string | null;
}

export const session = signal<SessionState>({
  status: authToken.value ? 'authenticating' : 'signed-out',
  me: null,
  error: null,
});

/** Run /api/me against the given token and drive `session` to its result. */
async function loadMe(idToken: string): Promise<void> {
  session.value = { status: 'authenticating', me: null, error: null };
  try {
    const me = await fetchMe(idToken);
    if (me.sessionToken) setToken(me.sessionToken);
    session.value = { status: me.mapped ? 'signed-in' : 'no-access', me, error: null };
  } catch (err) {
    // A 401 is not a fault — it is the ordinary end of a Google ID token's
    // one-hour life, and it happens to every user every day. Treating it as a
    // fault left the app on an error card with no sign-in button, reachable
    // only by clearing browser storage by hand. Drop the dead token and show
    // the button instead; Google's auto-select usually makes it one click or
    // none. A genuine failure (network, 500) still renders as a named fault,
    // because that one really is broken and saying "sign in again" would send
    // someone in circles.
    if (err instanceof HttpError && err.status === 401) {
      // Awaited, not fire-and-forget: the returned promise must not resolve
      // while the store is still being wiped and the status is still
      // 'authenticating'. A caller (or a test) that awaits sign-in is
      // waiting for the state machine to come to rest, not for the request.
      await expireSession();
      return;
    }
    session.value = {
      status: 'fault',
      me: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * The stored token is no longer accepted. Clear it and return to signed-out.
 *
 * Deliberately NOT signOut(): that also calls Google's disableAutoSelect(), so
 * using it here would punish an expired token by making the next sign-in
 * harder than the one before. The cached data still goes — it belonged to a
 * session that has ended, and the next person at a shared clinic desktop must
 * not inherit it.
 */
export async function expireSession(): Promise<void> {
  authToken.value = null;
  try {
    window.localStorage.removeItem('nvmanager.idToken');
  } catch {
    // Storage unavailable; the in-memory signal is already cleared.
  }
  await clearStore();
  forgetDataVersions(); // the saved versions name the last person (Cursor review #59)
  session.value = { status: 'signed-out', me: null, error: null };
}

/**
 * Re-read /api/me in place, WITHOUT dropping back through 'authenticating'.
 *
 * Used when the view-as lens changes (lib/viewAs.ts): /api/me answers
 * differently under `?as=`, and `me.locations` is what the clinic strip is
 * built from. Going via `loadMe` would blank the whole app to the
 * authenticating state and back for what is a scope change, not a sign-in.
 *
 * A 401 still ends the session, and any other failure is still a NAMED fault
 * (INV-9) rather than a silently stale screen.
 */
export async function reloadMe(): Promise<void> {
  const idToken = authToken.value;
  if (!idToken) return;
  try {
    const me = await fetchMe(idToken);
    if (me.sessionToken) setToken(me.sessionToken);
    session.value = { status: me.mapped ? 'signed-in' : 'no-access', me, error: null };
  } catch (err) {
    if (err instanceof HttpError && err.status === 401) {
      await expireSession();
      return;
    }
    session.value = {
      status: 'fault',
      me: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Called once at startup. If a token is already stored (previous sign-in),
 * re-validates it against /api/me rather than trusting it blindly. */
export function initSession(): void {
  if (authToken.value) {
    void loadMe(authToken.value);
  } else {
    session.value = { status: 'signed-out', me: null, error: null };
  }
}

/**
 * Called from the Google Identity Services credential callback.
 *
 * Resolves once `session` has come to rest (signed-in, no-access, fault, or
 * back to signed-out after a 401) — never while the store clear or /api/me
 * is still in flight. Google ignores the return value; tests await it instead
 * of guessing how long two IndexedDB clears take on a loaded machine (the
 * 30 ms guess they used to make lost under the full suite).
 */
export function handleSignIn(idToken: string): Promise<void> {
  setToken(idToken);
  // Show the named "Signing in..." state NOW. Waiting on IndexedDB before
  // flipping the status left a rejected /api/me on a blank page — the
  // store-clear can lose to a network error, and the shell had nothing to
  // render in the gap.
  session.value = { status: 'authenticating', me: null, error: null };
  // Drop the last login's cached pins (and everything else) before /api/me.
  // A shared clinic PC that signs in without Sign out must not keep the
  // previous person's starred people.
  return clearStore()
    .catch(() => undefined)
    .then(() => loadMe(idToken));
}

/** Test-only: reset session state between test files. */
export function __resetSessionForTests(): void {
  session.value = { status: authToken.value ? 'authenticating' : 'signed-out', me: null, error: null };
}
